import { css } from '@emotion/react';

/**
 * Global Styles
 * See: src/theme/tokens.js for token definitions
 */
export const baseStyles = css`
  /* Font imports - Inter is the primary font from design system */

  /* CSS Custom Properties for theming - Light theme (default) */
  :root {
    /* Colors - Background */
    --color-bg: #f7f7f7;
    --color-bg-container: #ffffff;
    --color-bg-box: rgba(29, 28, 33, 0.04);
    --color-bg-surface: #f1f1f3;
    --color-bg-grey-container: #e8e8e8;

    /* Colors - Border */
    --color-border: rgba(0, 0, 0, 0.1);
    --color-border-secondary: rgba(0, 0, 0, 0.04);

    /* Colors - Text/Info */
    --color-info: #1e1d29;
    --color-info-secondary: #3f424a;
    --color-info-tertiary: #727684;
    --color-info-quaternary: #a1a0ab;

    /* Colors - Primary */
    --color-primary: #04b17d;
    --color-primary-hover: #038f64;

    /* Colors - Semantic */
    --color-link: #04b17d;
    --color-code-bg: #f1f1f3;
    --color-code-text: #04b17d;
    --color-table-row-alt: #f8f8f8;
    --color-table-border: #0000001A;
    --color-diff-add-bg: #f0fff4;
    --color-diff-add-text: #22863a;
    --color-diff-remove-bg: #ffcccc;
    --color-diff-remove-text: #ff0000;

    /* Spacing */
    --gap-0x: 0px;
    --gap-1x: 5px;
    --gap-2x: 10px;
    --gap-3x: 15px;
    --gap-4x: 20px;
    --gap-5x: 25px;
    --gap-6x: 30px;
    --gap-8x: 40px;
    --gap-10x: 50px;
    --gap-20x: 100px;

    /* Border Radius */
    --radius-md: 6px;
    --radius-lg: 8px;
    --radius-xl: 12px;
    --radius-round: 1000px;

    /* Typography */
    --font-family-primary: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
    --font-family-mono: 'Roboto Mono', 'SF Mono', Menlo, monospace;
    --font-family-secondary: 'Lato', sans-serif;

    /* Font Sizes */
    --font-size-xs: 12px;
    --font-size-sm: 14px;
    --font-size-base: 16px;
    --font-size-lg: 18px;
    --font-size-xl: 21px;
    --font-size-2xl: 24px;
    --font-size-3xl: 28px;

    /* Line Heights */
    --line-height-tight: 1.3;
    --line-height-normal: 1.5;
    --line-height-relaxed: 1.625;

    /* Transitions */
    --transition-fast: 0.15s ease;
    --transition-normal: 0.3s ease;

    /* Shadows */
    --shadow-header: 0 1px 1px 0 rgba(116, 129, 141, 0.1);

    /* Layout sizes */
    --header-height: 64px;
    --header-height-mobile: 80px;
  }

  /* Dark theme overrides */
  [data-theme="dark"] {
    /* Colors - Background */
    --color-bg: #0c0e13;
    --color-bg-container: #181a1f;
    --color-bg-box: rgba(255, 255, 255, 0.06);
    --color-bg-surface: #1f2126;
    --color-bg-grey-container: #181a1f;

    /* Colors - Border */
    --color-border: #3d3f48;
    --color-border-secondary: #25272c;

    /* Colors - Text/Info */
    --color-info: rgba(255, 255, 255, 0.9);
    --color-info-secondary: #c9c7d0;
    --color-info-tertiary: #8f92a3;
    --color-info-quaternary: #7b798f;

    /* Colors - Primary (same in both themes) */
    --color-primary: #04b17d;
    --color-primary-hover: #1ed3c6;

    /* Colors - Semantic */
    --color-link: #04b17d;
    --color-code-bg: #1f2126;
    --color-code-text: #04b17d;
    --color-table-row-alt: #1f2126;
    --color-table-border: #3D3F48;
    --color-diff-add-bg: #0d2818;
    --color-diff-add-text: #4ade80;
    --color-diff-remove-bg: #2d1b1b;
    --color-diff-remove-text: #f87171;

    /* Shadows */
    --shadow-header: -1px 0px 4px 1px rgba(175, 158, 232, 0.4);
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  ::-webkit-input-placeholder {
    color: var(--color-info-quaternary);
  }

  :-ms-input-placeholder {
    color: var(--color-info-quaternary);
  }

  ::placeholder {
    color: var(--color-info-quaternary);
  }

  html,
  body {
    font-family: var(--font-family-primary);
    font-size: var(--font-size-base);
    line-height: var(--line-height-normal);
    scroll-behavior: smooth;
    background-color: var(--color-bg);
    color: var(--color-info-secondary);
    -webkit-font-smoothing: antialiased;
  }

  a {
    transition: color var(--transition-fast);
    color: var(--color-link);
    text-decoration: none;
  }
  a:hover {
    text-decoration: none;
  }

  /* Offset anchor scroll position to account for fixed header */
  h1, h2, h3, h4, h5, h6 {
    scroll-margin-top: calc(var(--header-height) + var(--gap-3x, 15px));
  }

  @media (max-width: 992px) {
    h1, h2, h3, h4, h5, h6 {
      scroll-margin-top: calc(var(--header-height-mobile) + var(--gap-3x, 15px));
    }
  }

  .visibleMobile {
    display: none;
  }
  .visibleMobileView {
    display: none !important;
  }
  .video-responsive {
    position: relative;
    padding-bottom: 56.2%;
  }
  .displayInline {
    display: inline-block;
  }
  .navBarToggle {
    border: 0px solid #fff;
    border-radius: 4px;
    width: 36px;
    height: 33px;
    position: absolute;
    right: 20px;
    padding: 8px 5px;
    display: none;
  }
  .navBarToggle .iconBar {
    display: block;
    width: 22px;
    height: 2px;
    border-radius: 1px;
    margin: 0 auto;
    margin-top: 4px;
    background-color: #001934;
  }
  .navBarToggle .iconBar:first-child {
    margin-top: 0px;
  }
  .video-responsive iframe {
    position: absolute;
    width: 100%;
    height: 100%;
  }

  .diffNewLine {
    color: var(--color-diff-add-text);
    background-color: var(--color-diff-add-bg);
  }

  .diffRemoveLine {
    color: var(--color-diff-remove-text);
    background-color: var(--color-diff-remove-bg);
  }
  .navBarParent {
    width: 100%;
    float: left;
    display: flex;
    align-items: center;
  }
  .divider {
    height: 30px;
    margin: 0 15px;
    border-right: 1px solid rgba(255, 255, 255, 0.3);
  }
  .navBarULRight {
    /* position: absolute;
  right: 30px; */
  }
  .githubIcon {
    width: 15px;
    margin-right: 5px;
  }

  .githubSection {
    display: flex;
    align-items: center;
    color: #000;
    opacity: 0.7;
  }

  .githubSection:hover {
    text-decoration: none;
    opacity: 1;
  }

  .navbar-default .navbar-toggle .icon-bar {
    background-color: #fff !important;
  }

  .navbar-default .navbar-toggle:focus,
  .navbar-default .navbar-toggle:hover {
    background-color: #001933;
  }

  .headerWrapper {
    border-bottom: 1px solid rgb(212, 218, 223);
    box-shadow: rgba(116, 129, 141, 0.1) 0px 1px 1px 0px;
    display: flex;
    align-items: center;
  }
  .formElement {
    background-color: transparent;
    padding: 4px;
    border-radius: 5px;
    position: relative;
  }
  .formElement:focus {
    outline: none;
    border: none;
  }
  .formElement svg path {
    fill: #2fd2c5;
  }
  .searchInput {
    width: 100%;
    background-color: rgba(28,211,198,.12) !important;
    border-width: 0 !important;
    color: #C2C2C2;
    padding: 10px;
    border-radius: 5px;
    color: #fff;
    opacity: .6;
    padding-left: 38px;
    max-width: 600px;
  }
  .searchInput:focus,
  .searchInput:visited,
  .searchInput:hover,
  .searchInput:focus-within {
    outline: none;
    border: 0;
  }
  .searchWrapper {
    padding-left: 0px;
    padding-right: 20px;
    flex: 1;
    position: relative;
  }
  .searchWrapper a {
    font-weight: 500;
  }
  .hitWrapper {
    background-color: #fff;
    padding: 0.7em 1em 0.4em;
    border-radius: 4px;
    position: absolute;
    width: 80vw;
    max-width: 30em;
    top: 40px;
    box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
    height: auto;
    max-height: 80vh;
    overflow: scroll;
    left: 0;
  }

  .hitWrapper ul li {
    padding: var(--gap-2x) var(--gap-3x);
    list-style-type: none;
  }
  .hitWrapper ul li:first-child {
    border-top: 0px;
  }
  .showResults {
    display: block;
  }
  .hideResults {
    display: none;
  }
  
  .headerTitle {
    height: auto;
    font-size: 16px;
    line-height: 1.5;
    font-weight: 300;
    color: #fff !important;
    margin-top: 16px;
    text-transform: uppercase;
  }
  .headerTitle a {
    color: #fff;
  }

  .headerTitle a:hover {
    text-decoration: none;
    opacity: 0.8;
  }
  .logoWrapper {
    padding: 21px 0;
    padding-left: 20px;
  }

  .logoContent {
    font-family: 'Roboto';
    margin-left: 16px;
    font-size: 28px;
    line-height: 1.5;
    font-weight: 500;
    padding-right: 10px;
  }

  /* Header section starts here */
  .removePadd {
    padding: 0 !important;
  }
  .navBarDefault {
    background-color: #001934;
    border-radius: 0;
    border-top: 0;
    margin-bottom: 0;
    border: 0;
    display: flex;
    align-items: center;
    box-shadow: -1px 0px 4px 1px rgba(175, 158, 232, 0.4);
    -webkit-box-shadow: -1px 0px 4px 1px rgba(175, 158, 232, 0.4);
    -moz-box-shadow: -1px 0px 4px 1px rgba(175, 158, 232, 0.8);
    -o-box-shadow: -1px 0px 4px 1px rgba(175, 158, 232, 0.4);
    z-index: 1;
    padding: 15px;
    position: relative;
    height: 80px;
  }
  .navBarHeader {
    min-width: 335px;
    padding-right: 20px;
    display: flex;
    align-items: center;
  }
  .navBarBrand {
    padding: 0px 0px;
    display: flex;
    align-items: center;
  }

  .navBarBrand img {
    width: 120px;
    margin-right: 6px;
    display: inline-block;
  }
  .navBarUL li {
    list-style-type: none;
  }
  .navBarUL {
    -webkit-overflow-scrolling: touch;
  }
  .navBarUL li a {
    font-family: 'Roboto';
    color: #fff !important;
    font-size: 16px;
    font-weight: 500;
    line-height: 1em;
    opacity: 1;
    padding: 10px 15px;
  }
  .navBarNav {
    display: flex;
    align-items: center;
  }
  .navBarUL li a img,
  .navBarUL li a .shareIcon {
    width: 20px;
  }
  .navBarUL li a:hover {
    opacity: 0.7;
  }
  .socialWrapper {
    display: flex;
    align-items: center;
  }
  .socialWrapper li {
    display: inline-block;
  }
  .socialWrapper li a {
    display: contents;
  }
  .discordBtn, .twitterBtn {
    border-radius: 4px;
    border: solid 1px #d1d2d3;
    background-color: #f1f5f8;
    width: 20px;
    height: 20px;
    padding-top: 2px;
    margin-left: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: .8;
    cursor: pointer;
  }
  .twitterBtn img {
    width: 12px !important;
  }
  .discordBtn img {
    width: 10px !important;
  }
  .discordBtn:hover, .twitterBtn:hover {
    opacity: 1;
  }
  .discordBtn {
    img {
      width: 10px;
    }
  }
  /* Header section ends here */

  /* ============================================
   * SIDEBAR STYLES - Updated to match Figma design
   * Menu Item Level 1: node-id=1-6439
   * Menu Item Level 2: node-id=1-6513
   * ============================================ */

  .sidebarTitle {
    background-color: var(--color-bg-surface);
    padding: var(--gap-4x, 20px) var(--gap-3x, 15px);
    font-family: var(--font-family-primary, 'Inter', sans-serif);
    font-size: var(--font-size-lg, 18px);
    font-weight: 600;
    color: var(--color-info);
    display: flex;
    align-items: center;
    border-bottom: 1px solid var(--color-border-secondary);
  }

  .sideBarShow {
    display: none;
  }

  .sidebarTitle a {
    color: var(--color-info);
    text-decoration: none;
  }

  .greenCircle {
    width: 8px;
    height: 8px;
    background-color: var(--color-primary);
    border-radius: 50%;
    margin: 0 12px;
  }

  .sideBarUL {
    margin-top: var(--gap-3x, 15px);
    padding: 0 var(--gap-3x, 15px);
  }

  .sideBarUL li {
    list-style-type: none;
    width: auto;
  }

  /* Level 1 Menu Item - Parent items with collapse */
  .sideBarUL .item {
    list-style: none;
    padding: 0;
    margin-bottom: var(--gap-2x, 10px);
  }

  .sideBarUL .item > a {
    color: var(--color-info-secondary);
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    width: 100%;
    padding: var(--gap-2x, 10px) var(--gap-3x, 15px);
    font-family: var(--font-family-primary, 'Inter', sans-serif);
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    border-radius: var(--radius-md, 6px);
    border: 1px solid transparent;
    transition: all var(--transition-fast, 0.15s ease);
  }

  /* Level 1 - Hover state */
  .sideBarUL .item > a:hover {
    color: var(--color-info);
    border-radius: 6px;
    border: 1px solid var(--color-border, rgba(0, 0, 0, 0.10));
    opacity: 0.9;
    background-color: transparent;
  }

  /* Level 1 - Active/Selected state (expanded) */
  .sideBarUL .item.active > a {
    color: var(--color-info);
    background-color: var(--color-bg-grey-container, #E8E8E8);
    border-color: transparent;
  }

  /* Collapse button styling */
  .hideFrontLine .collapser {
    background: transparent;
    border: none;
    outline: none;
    padding: 0;
    margin-left: var(--gap-2x, 10px);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform var(--transition-fast, 0.15s ease);
  }

  .hideFrontLine .collapser svg {
    width: 20px;
    height: 20px;
  }

  .hideFrontLine .collapser svg path {
    fill: var(--color-info-secondary);
  }

  .sideBarUL .item > a:hover .collapser svg path,
  .sideBarUL .item.active > a .collapser svg path {
    fill: var(--color-info);
  }

  /* Level 2 Menu Item - Child items with indicator */
  .sideBarUL .item .item {
    margin-left: 0;
    margin-bottom: 0;
  }

  .sideBarUL .item .item > a {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: var(--gap-2x, 10px);
    padding: var(--gap-1x, 5px) 0 var(--gap-1x, 5px) var(--gap-3x, 15px);
    font-size: 13px;
    font-weight: 500;
    color: var(--color-info-tertiary);
    border: none;
    border-radius: 0;
    background: transparent;

    &[data-level]="1" {
      padding: var(--gap-1x, 5px) 0 var(--gap-1x, 5px) var(--gap-3x, 15px);
    }
  }

  .sideBarUL .item .item > a span {
    flex-grow: 0;
  }

  /* Level 2 - Left indicator (hidden by default) */
  .sideBarUL .item .item > a::before {
    content: '';
    width: 3px;
    height: 20px;
    border-radius: var(--radius-xl, 12px);
    background-color: transparent;
    flex-shrink: 0;
    transition: background-color var(--transition-fast, 0.15s ease);
  }

  /* Level 2 - Hover state */
  .sideBarUL .item .item > a:hover {
    color: var(--color-info-secondary);
    background: transparent;
    border: none;
  }

  /* Level 2 - Active/Selected state with green indicator */
  .sideBarUL .item .item.active > a {
    color: var(--color-info-secondary);
    // background: transparent;
  }

  .sideBarUL .item .item.active > a::before {
    background-color: var(--color-primary);
  }

  /* First level container */
  .firstLevel > ul {
    margin: 0 !important;
    padding: 0 !important;
  }

  .firstLevel > ul > .item {
    margin-left: 0 !important;
  }

  /* Nested items container */
  .sideBarUL .item ul {
    margin-top: 2px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-left: var(--gap-1x);
  }

  /* Legacy styles - keep for compatibility but override with above */
  .showFrontLine .item > a:hover {
    background-color: transparent;
  }

  .showFrontLine .active > a {
    background-color: var(--color-bg-grey-container);
  }

  .showFrontLine .item .item {
    border-left: none;
    padding: 0;
    width: 100% !important;
  }

  .showFrontLine .item .active > a {
    border: none;
    background-color: transparent !important;
    color: var(--color-info-secondary);
  }

  .titleWrapper {
    display: flex;
    align-items: center;
    padding-bottom: 40px;
    border-bottom: 1px solid rgb(230, 236, 241);
    margin-bottom: 32px;
  }

  .gitBtn {
    height: 30px;
    min-height: 30px;
    display: flex;
    align-items: center;
  }

  .gitBtn img {
    width: 15px;
    display: inline-block;
    margin-right: 5px;
  }

  .addPaddTopBottom {
    padding: 50px 0;
  }

  .preRightWrapper {
    display: block;
    margin: 0px;
    flex: 1 1 0%;
    padding: 16px;
    text-align: right;
  }

  .smallContent {
    display: block;
    margin: 0px;
    padding: 0px;
    color: #6e6e6e;
  }

  .smallContent span {
    font-size: 12px;
    line-height: 1.625;
    font-weight: 400;
  }

  /* **************************** */

  .nextRightWrapper {
    display: block;
    margin: 0px;
    padding: 16px;
    flex: 1 1 0%;
  }
  /* Image styling */
  img {
    max-width: 100%;
  }
  /* end image */
  .githubBtn {
    display: flex;
    align-items: center;
    font-size: 16px;
    padding: 10px 0px;
    padding-left: 15px;
    max-height: 40px;
  }
  .githubBtn span span {
    display: flex;
    align-items: center;
  }

  .communitySection {
    font-size: 24px;
    font-weight: 700;
  }
  .authorSection {
    padding: 20px 0;
  }
  .authorSection,
  .authorName {
    display: flex;
    align-items: center;
  }
  .authorImg img {
    width: 75px;
    height: 75px;
    border-radius: 50%;
    min-width: 75px;
    max-width: 75px;
    min-height: 75px;
    max-height: 75px;
  }
  .authorDetails {
    padding-left: 10px;
  }
  .authorDesc {
    padding-top: 5px;
    font-size: 14px;
  }
  .authorName img {
    margin-left: 10px;
    display: inline-block;
    width: 20px;
  }
  .authorName img:hover {
    opacity: 0.7;
  }
  .poweredBy {
    font-size: 0.6em;
    text-align: end;
    padding: 0;
  }
  .topnav {
    -webkit-transition: top 0.5s, bottom 0.5s;
  }

  /* ============================================
   * RESPONSIVE UTILITY CLASSES
   * Matches Figma breakpoints
   * ============================================ */

  /* Hide on mobile (≤768px) */
  @media (max-width: 768px) {
    .hiddenMobile {
      display: none !important;
    }
  }

  /* Hide on tablet (≤993px) */
  @media (max-width: 993px) {
    .hiddenTablet {
      display: none !important;
    }
  }

  /* Show only on mobile (≤768px) */
  .visibleMobile {
    display: none;
  }

  @media (max-width: 768px) {
    .visibleMobile {
      display: block !important;
    }
  }

  /* ============================================
   * RESPONSIVE BREAKPOINTS
   * ≤576px: Extra Small/Mobile
   * 577-768px: Small/Mobile Landscape
   * 769-992px: Medium/Tablet
   * 993-1440px: Large/Desktop
   * 1441-1920px: Extra Large
   * >1920px: Ultra Wide
   * ============================================ */

  /* Extra Small - Mobile (≤576px) */
  @media (max-width: 576px) {
    .titleWrapper {
      padding: 0 var(--gap-3x, 15px);
      margin-bottom: var(--gap-4x, 20px);
    }

    .addPaddTopBottom {
      padding: var(--gap-6x, 30px) 0;
    }

    .sidebarTitle {
      font-size: var(--font-size-base, 16px);
      padding: var(--gap-3x, 15px);
    }
  }

  /* Small - Mobile Landscape (577-768px) */
  @media (min-width: 577px) and (max-width: 768px) {
    .titleWrapper {
      padding: 0 var(--gap-4x, 20px);
    }
  }

  /* Medium - Tablet (769-992px) */
  @media (max-width: 992px) {
    .sideBarUL .item ul {
      margin-top: var(--gap-2x, 10px);
      gap: var(--gap-2x, 10px);
    }

    .sideBarUL .item .item>a{
      font-size: 18px !important;
      padding-top: var(--gap-2x, 10px);
      padding-bottom: var(--gap-2x, 10px);
    }

    .navBarDefault {
      padding: var(--gap-2x, 10px);
    }

    .navBarHeader {
      min-width: 240px;
      flex: initial;
    }

    .divider {
      margin: 0 var(--gap-1x, 5px);
      height: 20px;
    }

    .hitWrapper {
      width: auto;
      max-width: unset;
    }
  }
`;

export const mdxStyles = css`
  .paragraph {
    font-size: var(--font-size-sm, 14px);
    font-weight: 400;
    line-height: var(--line-height-normal, 1.5);
    letter-spacing: 0.07px;
    margin-bottom: var(--gap-3x, 15px);
  }

  /* Images */
  .img {
    max-width: 100%;
    height: auto;
    border-radius: var(--radius-md, 6px);
    margin-bottom: var(--gap-2x, 10px);
  }

  /* Image captions */
  .img + em {
    display: block;
    font-family: var(--font-family-primary, 'Inter', sans-serif);
    font-style: italic;
    color: var(--color-info-secondary);
    margin-bottom: var(--gap-3x, 15px);
  }

  /* Code blocks - matches Figma design */
  .pre {
    font-family: var(--font-family-mono);
    font-size: 13px;
    line-height: var(--line-height-tight, 1.3);
    padding: var(--gap-4x, 20px);
    overflow: auto;
    background-color: var(--color-bg-surface);
    border-radius: var(--radius-lg, 8px);
    color: var(--color-code-text, #04b17d);
  }

  .pre code {
    background: transparent;
    padding: 0;
    border-radius: 0;
    font-size: inherit;
    color: inherit;
  }

  .blockquote {
    font-family: var(--font-family-secondary, 'Lato', sans-serif);
    font-style: italic;
    color: var(--color-info-tertiary);
    margin: var(--gap-3x, 15px) 0 var(--gap-4x, 20px) 0;
    padding: var(--gap-4x, 20px) var(--gap-4x, 20px) var(--gap-4x, 20px) 90px;
    position: relative;
    border-top: 1px solid var(--color-border);
    border-bottom: 1px solid var(--color-border);
    min-height: 32px;
    box-sizing: content-box;
  }

  .blockquote::before {
    content: '';
    position: absolute;
    left: var(--gap-4x, 20px);
    top: 16px;
    width: 51px;
    height: 39px;
    background-image: url("data:image/svg+xml,%3Csvg width='51' height='39' viewBox='0 0 51 39' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11.5915 15.5531C11.0246 15.5531 10.4805 15.6417 9.93895 15.7224C10.1144 15.118 10.2949 14.5032 10.5847 13.9509C10.8746 13.1485 11.3271 12.4529 11.7771 11.7521C12.1534 10.994 12.817 10.4808 13.3051 9.83206C13.8162 9.2016 14.5128 8.78216 15.0645 8.25852C15.606 7.71142 16.3154 7.43787 16.8798 7.0523C17.4696 6.70581 17.9832 6.32284 18.5324 6.14048L19.9027 5.56212L21.1079 5.0489L19.8748 0L18.3569 0.375149C17.8713 0.500199 17.2789 0.646091 16.6052 0.82064C15.9162 0.950901 15.1814 1.30782 14.3628 1.63347C13.5543 2.00341 12.6187 2.25351 11.7492 2.84749C10.8746 3.41543 9.86522 3.88958 8.97536 4.6503C8.11348 5.43447 7.07362 6.11443 6.30581 7.11222C5.46681 8.04489 4.63797 9.02445 3.99474 10.1395C3.24981 11.2024 2.74386 12.3695 2.20995 13.5236C1.72689 14.6778 1.3379 15.8579 1.02009 17.0042C0.417537 19.302 0.148039 21.4852 0.0437991 23.3531C-0.0426436 25.2236 0.00820507 26.779 0.114987 27.9044C0.153124 28.4359 0.224312 28.9517 0.275161 29.3086L0.338721 29.7463L0.404824 29.7307C0.857024 31.8951 1.898 33.8842 3.40734 35.4678C4.91668 37.0514 6.83271 38.1647 8.93377 38.6791C11.0348 39.1935 13.2351 39.0878 15.28 38.3743C17.3249 37.6608 19.1309 36.3687 20.489 34.6474C21.8472 32.9261 22.7021 30.846 22.9547 28.6477C23.2074 26.4493 22.8475 24.2226 21.9167 22.2251C20.9859 20.2276 19.5222 18.5409 17.695 17.3602C15.8678 16.1795 13.7517 15.553 11.5915 15.5531ZM39.5583 15.5531C38.9913 15.5531 38.4472 15.6417 37.9057 15.7224C38.0811 15.118 38.2616 14.5032 38.5515 13.9509C38.8413 13.1485 39.2939 12.4529 39.7439 11.7521C40.1202 10.994 40.7837 10.4808 41.2719 9.83206C41.7829 9.2016 42.4795 8.78216 43.0312 8.25852C43.5728 7.71142 44.2821 7.43787 44.8465 7.0523C45.4364 6.70581 45.95 6.32284 46.4991 6.14048L47.8695 5.56212L49.0746 5.0489L47.8415 0L46.3237 0.375149C45.8381 0.500199 45.2457 0.646091 44.572 0.82064C43.883 0.950901 43.1482 1.30782 42.3295 1.63347C41.5236 2.00601 40.5854 2.25351 39.7159 2.8501C38.8413 3.41804 37.832 3.89218 36.9421 4.6529C36.0802 5.43707 35.0404 6.11703 34.2726 7.11222C33.4336 8.04489 32.6047 9.02445 31.9615 10.1395C31.2166 11.2024 30.7106 12.3695 30.1767 13.5236C29.6936 14.6778 29.3047 15.8579 28.9868 17.0042C28.3843 19.302 28.1148 21.4852 28.0106 23.3531C27.9241 25.2236 27.975 26.779 28.0817 27.9044C28.1199 28.4359 28.1911 28.9517 28.2419 29.3086L28.3055 29.7463L28.3716 29.7307C28.8238 31.8951 29.8648 33.8842 31.3741 35.4678C32.8834 37.0514 34.7995 38.1647 36.9005 38.6791C39.0016 39.1935 41.2018 39.0878 43.2467 38.3743C45.2916 37.6608 47.0976 36.3687 48.4558 34.6474C49.814 32.9261 50.6688 30.846 50.9215 28.6477C51.1741 26.4493 50.8142 24.2226 49.8834 22.2251C48.9526 20.2276 47.489 18.5409 45.6618 17.3602C43.8346 16.1795 41.7185 15.553 39.5583 15.5531Z' fill='%23A0A3AB'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-size: contain;
  }

  .blockquote p {
    margin: 0;
    color: inherit;
  }

   /* tables.css */
  .table {
    padding: 0;
    border-collapse: collapse;
    min-width: 100%;
    margin: var(--gap-4x, 20px) 0;
    font-size: var(--font-size-sm, 14px);
  }

  .table tr {
    border-top: 1px solid var(--color-table-border);
    margin: 0;
    padding: 0;
  }

  .table tr th {
    font-weight: 600;
    border: 1px solid var(--color-table-border);
    text-align: left;
    margin: 0;
    padding: var(--gap-2x, 10px);
    color: var(--color-info);
  }

  .table tr td {
    border: 1px solid var(--color-table-border);
    text-align: left;
    margin: 0;
    padding: var(--gap-2x, 10px);
    color: var(--color-info-secondary);
  }

  .table tr th :first-child,
  .table tr td :first-child {
    margin-top: 0;
  }

  .table tr th :last-child,
  .table tr td :last-child {
    margin-bottom: 0;
  }
  /* end - tables.css */

  .heading1, .heading2, .heading3, .heading4, .heading5, .heading6 {
    margin-top: var(--gap-2x, 10px);
    margin-bottom: var(--gap-2x, 10px);
    font-weight: 600;
    line-height: var(--line-height-tight, 1.3);
    color: var(--color-info);
  }

  .heading1 {
    font-size: var(--font-size-3xl, 28px);
    margin-top: var(--gap-3x, 15px);
    margin-bottom: var(--gap-3x, 15px);
  }

  .heading2 {
    font-size: var(--font-size-2xl, 24px);
  }

  .heading3 {
    font-size: var(--font-size-xl, 21px);
  }

  .heading4 {
    font-size: var(--font-size-lg, 18px);
  }

  .heading5 {
    font-size: var(--font-size-base, 16px);
  }

  .heading6 {
    font-size: var(--font-size-sm, 14px);
  }

  hr {
    border-top-color: var(--color-border);
    border-bottom: none;
  }

  /* Lists */
  ul,
  ol {
    margin: 0 0 var(--gap-4x, 20px) 0;
    padding: 0 0 0 var(--gap-5x, 24px);

    li {
      font-size: var(--font-size-sm, 14px);
      line-height: var(--line-height-normal, 1.5);
      margin-bottom: 0;
    }

    /* Nested lists */
    ul, ol {
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  /* Links */
  .link {
    color: var(--color-link, #04b17d);
    letter-spacing: 0.07px;
    text-decoration: underline;
    text-underline-offset: 4px;
    transition: color var(--transition-fast, 0.15s ease);

    &:hover {
      text-decoration: underline;
      text-underline-offset: 4px;
      color: var(--color-primary-hover);
    }
  }

  /* Inline code */
  code {
    font-family: var(--font-family-mono, 'Input Mono', monospace);
    background-color: var(--color-bg-surface);
    border-radius: var(--radius-md, 6px);
    padding: 2px 8px;
    font-size: var(--font-size-sm, 14px);
    color: var(--color-code-text, #04b17d);
    border: none;
  }

  /* Extra Small - Mobile (≤576px) */
  @media (max-width: 576px) {
    .blockquote::before {
      width: 35px;
      height: 27px;
      left: var(--gap-2x, 10px);
    }
  }
`
